import { z } from "zod";
import {
  bookingBlockReasonSchema,
  bookingSourceSchema,
  freeCancellationModeSchema,
  lateCancelRuleSchema,
  policyActionSchema,
  policyActorSchema,
  policyPenaltySchema,
  policyReasonSchema,
} from "../enums.ts";

/**
 * The subset of a `policies` row the engine needs. Nothing here is a database handle, so the mobile
 * app can hold one in memory and preview an outcome with no round trip.
 */
export const policySchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().optional(),
  /** Hours before the session start where a member can still cancel or reschedule for free. */
  cancelCutoffHours: z.number().int().min(0),
  lateCancelRule: lateCancelRuleSchema,
  /** Share of the credit returned on a late cancel, in basis points. Only read when the rule is "partial". */
  lateCancelPartialBps: z.number().int().min(0).max(10_000).nullable().default(null),
  /** Null means no limit. */
  rescheduleLimitPerBooking: z.number().int().min(0).nullable().default(null),
  /** Null means no limit. */
  rescheduleLimitPerMonth: z.number().int().min(0).nullable().default(null),
  /** Minutes before the session start where new bookings stop being accepted. */
  minBookAheadMin: z.number().int().min(0).default(0),
  /** How far into the future a member can book. */
  maxBookAheadDays: z.number().int().min(1).default(56),
});
export type Policy = z.infer<typeof policySchema>;

/**
 * Used when a group has no policy attached. Generous on purpose: surprises erode trust. The API
 * books and cancels with it, and the member home states it, so it lives here once.
 */
export const DEFAULT_POLICY: Policy = {
  cancelCutoffHours: 24,
  lateCancelRule: "lose_credit",
  lateCancelPartialBps: null,
  rescheduleLimitPerBooking: null,
  rescheduleLimitPerMonth: null,
  minBookAheadMin: 0,
  maxBookAheadDays: 56,
};

export const policyInputSchema = z.object({
  policy: policySchema,
  sessionStartsAt: z.coerce.date(),
  now: z.coerce.date(),
  actor: policyActorSchema,
  bookingSource: bookingSourceSchema,
  /** How many times this specific booking has already been rescheduled. */
  rescheduleCount: z.number().int().min(0),
  /** How many reschedules this member has made across all bookings in the session's local month. */
  monthlyRescheduleCount: z.number().int().min(0),
  action: policyActionSchema,
  /*
   * The cancellation allowance, which belongs to the package the booking was paid from rather than
   * to the group's policy. Two members at one business can hold different packages and therefore
   * different allowances, which a policy row cannot express.
   *
   * Both default to the pre-allowance behaviour, so every existing caller keeps working: null means
   * unlimited, which is what every business had before packages could carry a number.
   */
  /**
   * How many cancellations this package includes. Null or absent means unlimited.
   *
   * Optional rather than defaulted, so that every existing caller still type checks and an older
   * client that knows nothing about allowances gets exactly the behaviour it had before.
   */
  cancellationAllowance: z.number().int().min(0).nullable().optional(),
  /** How many of them have already been spent on this purchase. Absent counts as none. */
  cancellationsUsed: z.number().int().min(0).optional(),
  /**
   * Which cancellations spend the allowance (see `freeCancellationModes`). Absent means
   * `any_cancel`, the behaviour before the mode existed. Only read when the allowance is a number.
   */
  freeCancellationMode: freeCancellationModeSchema.optional(),
});
export type PolicyInput = z.infer<typeof policyInputSchema>;

export const policyOutcomeSchema = z.object({
  allowed: z.boolean(),
  penalty: policyPenaltySchema,
  /**
   * Whole credits to return to the member: 0 or 1 for an open booking, always 0 for an enrollment
   * booking. Credits are indivisible integers in V1, so a partial rule resolves to a whole credit
   * through `partialBps` (see `resolvePartialCredits`).
   */
  creditDelta: z.number().int(),
  /** Present only when `penalty` is "partial": the configured share, for copy and for the ledger note. */
  partialBps: z.number().int().min(0).max(10_000).optional(),
  reason: policyReasonSchema,
  /** Human copy shown verbatim in the confirmation sheet before the member commits. */
  explanation: z.string(),
});
export type PolicyOutcome = z.infer<typeof policyOutcomeSchema>;

export const bookingPolicyInputSchema = z.object({
  policy: policySchema,
  sessionStartsAt: z.coerce.date(),
  now: z.coerce.date(),
  /** A cancelled session can never be booked, whatever the policy says. */
  sessionCancelled: z.boolean().default(false),
});
export type BookingPolicyInput = z.infer<typeof bookingPolicyInputSchema>;

export const bookingPolicyOutcomeSchema = z.object({
  allowed: z.boolean(),
  reason: bookingBlockReasonSchema.optional(),
  explanation: z.string(),
  /** With `too_far_ahead`: the moment the session opens for booking (its start less the window). */
  opensAt: z.date().optional(),
});
export type BookingPolicyOutcome = z.infer<typeof bookingPolicyOutcomeSchema>;
